import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { translate } from '@jsverse/transloco';
import { EMPTY, Observable } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { InventoryService } from 'src/app/inventory/services/inventory.service';
import {
  ICreateInventoryDeviceRequest,
  ICreateInventoryDeviceServiceRequest,
  IInventoryDeviceDetails,
  IUpdateInventoryDeviceRequest,
  IUpdateInventoryDeviceServiceRequest
} from 'src/app/inventory/models/inventory-device';
import { AppNotificationService } from 'src/app/shared/services/notification.service';

export interface InventoryState {
  device: IInventoryDeviceDetails | null;
  loading: boolean;
}

/**
 * Component store for one inventory device (detail page). Service mutations replace the whole device
 * with the response of the backend, so the recalculated next service date is always up to date.
 */
@Injectable()
export class InventoryStore extends ComponentStore<InventoryState> {

  constructor(
    private inventoryService: InventoryService,
    private appnotificationService: AppNotificationService) {
      super({
        device: null,
        loading: false
      });
  }

  readonly device$: Observable<IInventoryDeviceDetails | null> = this.select(state => state.device);
  readonly loading$: Observable<boolean> = this.select(state => state.loading);

  // Effect to load an inventory device by ID
  readonly getInventoryDeviceById = this.effect<string>(id$ =>
    id$.pipe(
      tap(() => this.patchState({ loading: true })),
      switchMap(id =>
        this.inventoryService.getInventoryDeviceById(id).pipe(
          tap(device => this.patchState({ device, loading: false })),
          catchError(() => {
            this.patchState({ loading: false });
            return EMPTY;
          })
        )
      )
    )
  );

  // Effect to create an inventory device
  readonly createInventoryDevice = this.effect<{ request: ICreateInventoryDeviceRequest, onSuccess?: (device: IInventoryDeviceDetails) => void }>(params$ =>
    params$.pipe(
      tap(() => this.patchState({ loading: true })),
      switchMap(({ request, onSuccess }) =>
        this.inventoryService.createInventoryDevice(request).pipe(
          tap(device => {
            this.patchState({ device, loading: false });
            this.appnotificationService.notification$.next({ message: translate('inventory.createSuccess'), style: 'success' });
            onSuccess?.(device);
          }),
          catchError(() => {
            this.patchState({ loading: false });
            return EMPTY;
          })
        )
      )
    )
  );

  // Effect to update an inventory device
  readonly updateInventoryDevice = this.effect<IUpdateInventoryDeviceRequest>(request$ =>
    request$.pipe(
      tap(() => this.patchState({ loading: true })),
      switchMap(request =>
        this.inventoryService.updateInventoryDevice(request).pipe(
          tap(device => {
            this.patchState({ device, loading: false });
            this.appnotificationService.notification$.next({ message: translate('inventory.updateSuccess'), style: 'success' });
          }),
          catchError(() => {
            this.patchState({ loading: false });
            return EMPTY;
          })
        )
      )
    )
  );

  // Effect to delete an inventory device (including its services and files)
  readonly deleteInventoryDevice = this.effect<{ id: string, onSuccess?: () => void }>(params$ =>
    params$.pipe(
      tap(() => this.patchState({ loading: true })),
      switchMap(({ id, onSuccess }) =>
        this.inventoryService.deleteInventoryDevice(id).pipe(
          tap(() => {
            this.patchState({ device: null, loading: false });
            this.appnotificationService.notification$.next({ message: translate('inventory.deleteSuccess'), style: 'success' });
            onSuccess?.();
          }),
          catchError(() => {
            this.patchState({ loading: false });
            return EMPTY;
          })
        )
      )
    )
  );

  // Effect to record a service for the device
  readonly addService = this.effect<ICreateInventoryDeviceServiceRequest>(request$ =>
    request$.pipe(
      tap(() => this.patchState({ loading: true })),
      switchMap(request =>
        this.inventoryService.createInventoryDeviceService(request).pipe(
          tap(device => {
            this.patchState({ device, loading: false });
            this.appnotificationService.notification$.next({ message: translate('inventory.detail.services.createSuccess'), style: 'success' });
          }),
          catchError(() => {
            this.patchState({ loading: false });
            return EMPTY;
          })
        )
      )
    )
  );

  // Effect to update a service of the device
  readonly updateService = this.effect<IUpdateInventoryDeviceServiceRequest>(request$ =>
    request$.pipe(
      tap(() => this.patchState({ loading: true })),
      switchMap(request =>
        this.inventoryService.updateInventoryDeviceService(request).pipe(
          tap(device => {
            this.patchState({ device, loading: false });
            this.appnotificationService.notification$.next({ message: translate('inventory.detail.services.updateSuccess'), style: 'success' });
          }),
          catchError(() => {
            this.patchState({ loading: false });
            return EMPTY;
          })
        )
      )
    )
  );

  // Effect to delete a service of the device
  readonly removeService = this.effect<string>(id$ =>
    id$.pipe(
      tap(() => this.patchState({ loading: true })),
      switchMap(id =>
        this.inventoryService.deleteInventoryDeviceService(id).pipe(
          tap(device => {
            this.patchState({ device, loading: false });
            this.appnotificationService.notification$.next({ message: translate('inventory.detail.services.deleteSuccess'), style: 'success' });
          }),
          catchError(() => {
            this.patchState({ loading: false });
            return EMPTY;
          })
        )
      )
    )
  );
}